import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Package,
  Wallet,
  Calculator,
  Loader2,
  CalendarDays,
  Printer,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  Percent
} from 'lucide-react';

const ProfitReportPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  
  // Default to current month
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/reports/profit`, {
        params: { start_date: startDate, end_date: endDate },
        headers: getAuthHeader()
      });
      setReport(res.data);
    } catch (error) {
      toast.error('حدث خطأ في تحميل التقرير');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fc = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const handlePrint = () => {
    if (!report) return;

    const productRows = report.products.map(p => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${p.product_name}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${p.quantity_sold}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${fc(p.cost_price)} €</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${fc(p.avg_sell_price)} €</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${fc(p.revenue)} €</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${fc(p.cost)} €</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:bold;color:${p.profit >= 0 ? '#059669' : '#dc2626'}">${fc(p.profit)} €</td>
      </tr>
    `).join('');

    const expenseRows = Object.entries(report.expenses_by_category).map(([cat, amount]) => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${cat}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center;color:#dc2626">${fc(amount)} €</td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>تقرير الأرباح</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; direction: rtl; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th { background: #1e40af; color: white; padding: 10px; text-align: right; }
          .summary-box { display: inline-block; padding: 15px 25px; margin: 5px; border-radius: 8px; text-align: center; }
          .green { background: #d1fae5; color: #059669; }
          .red { background: #fee2e2; color: #dc2626; }
          .blue { background: #dbeafe; color: #1e40af; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1 style="text-align:center;color:#1e40af">📊 تقرير الأرباح</h1>
        <p style="text-align:center;color:#666">من ${startDate} إلى ${endDate}</p>
        
        <div style="text-align:center;margin:20px 0">
          <div class="summary-box blue">
            <div style="font-size:12px">إجمالي المبيعات</div>
            <div style="font-size:24px;font-weight:bold">${fc(report.summary.total_revenue)} €</div>
          </div>
          <div class="summary-box red">
            <div style="font-size:12px">تكلفة البضاعة</div>
            <div style="font-size:24px;font-weight:bold">${fc(report.summary.total_cost)} €</div>
          </div>
          <div class="summary-box green">
            <div style="font-size:12px">إجمالي الربح</div>
            <div style="font-size:24px;font-weight:bold">${fc(report.summary.gross_profit)} €</div>
          </div>
          <div class="summary-box red">
            <div style="font-size:12px">المصروفات</div>
            <div style="font-size:24px;font-weight:bold">${fc(report.summary.total_expenses)} €</div>
          </div>
          <div class="summary-box" style="background:${report.summary.net_profit >= 0 ? '#059669' : '#dc2626'};color:white">
            <div style="font-size:12px">صافي الربح</div>
            <div style="font-size:24px;font-weight:bold">${fc(report.summary.net_profit)} €</div>
          </div>
        </div>

        <h3>📦 تفاصيل المنتجات</h3>
        <table>
          <thead>
            <tr>
              <th>المنتج</th>
              <th>الكمية المباعة</th>
              <th>سعر الشراء</th>
              <th>سعر البيع</th>
              <th>الإيرادات</th>
              <th>التكلفة</th>
              <th>الربح</th>
            </tr>
          </thead>
          <tbody>${productRows}</tbody>
        </table>

        ${Object.keys(report.expenses_by_category).length > 0 ? `
          <h3>💸 المصروفات حسب الفئة</h3>
          <table>
            <thead>
              <tr>
                <th>الفئة</th>
                <th>المبلغ</th>
              </tr>
            </thead>
            <tbody>${expenseRows}</tbody>
          </table>
        ` : ''}

        <div style="text-align:center;margin-top:30px;padding:15px;background:#f3f4f6;border-radius:8px">
          <p>هامش الربح الإجمالي: <strong>${report.summary.profit_margin}%</strong> | هامش الربح الصافي: <strong>${report.summary.net_margin}%</strong></p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="profit-report-page" className="space-y-5" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-bold text-stone-900 flex items-center gap-2">
          <Calculator className="w-6 h-6 text-green-600" />
          تقرير الأرباح
        </h1>
        <div className="flex gap-2">
          <Button onClick={handlePrint} variant="outline" size="sm" className="gap-1" disabled={!report}>
            <Printer className="w-4 h-4" /> طباعة
          </Button>
        </div>
      </div>

      {/* Date Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label>من تاريخ</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <Label>إلى تاريخ</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={fetchReport} className="gap-1">
              <CalendarDays className="w-4 h-4" /> عرض التقرير
            </Button>
          </div>
        </CardContent>
      </Card>

      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4 text-center">
                <DollarSign className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                <p className="text-xs text-blue-600">إجمالي المبيعات</p>
                <p className="text-xl font-bold text-blue-700">{fc(report.summary.total_revenue)} €</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4 text-center">
                <Package className="w-6 h-6 mx-auto text-amber-600 mb-1" />
                <p className="text-xs text-amber-600">تكلفة البضاعة</p>
                <p className="text-xl font-bold text-amber-700">{fc(report.summary.total_cost)} €</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
                <p className="text-xs text-emerald-600">إجمالي الربح</p>
                <p className="text-xl font-bold text-emerald-700">{fc(report.summary.gross_profit)} €</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4 text-center">
                <Wallet className="w-6 h-6 mx-auto text-red-600 mb-1" />
                <p className="text-xs text-red-600">المصروفات</p>
                <p className="text-xl font-bold text-red-700">{fc(report.summary.total_expenses)} €</p>
              </CardContent>
            </Card>

            <Card className={`bg-gradient-to-br ${report.summary.net_profit >= 0 ? 'from-green-100 to-green-200 border-green-300' : 'from-red-100 to-red-200 border-red-300'}`}>
              <CardContent className="p-4 text-center">
                {report.summary.net_profit >= 0 ? (
                  <ArrowUpRight className="w-6 h-6 mx-auto text-green-600 mb-1" />
                ) : (
                  <ArrowDownRight className="w-6 h-6 mx-auto text-red-600 mb-1" />
                )}
                <p className={`text-xs ${report.summary.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>صافي الربح</p>
                <p className={`text-xl font-bold ${report.summary.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fc(report.summary.net_profit)} €</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4 text-center">
                <Percent className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                <p className="text-xs text-purple-600">هامش الربح</p>
                <p className="text-xl font-bold text-purple-700">{report.summary.profit_margin}%</p>
              </CardContent>
            </Card>
          </div>

          {/* Products Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="w-5 h-5 text-blue-600" />
                تفاصيل الأرباح حسب المنتج
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>المنتج</TableHead>
                    <TableHead className="text-center">الكمية المباعة</TableHead>
                    <TableHead className="text-center">سعر الشراء</TableHead>
                    <TableHead className="text-center">سعر البيع</TableHead>
                    <TableHead className="text-center">الإيرادات</TableHead>
                    <TableHead className="text-center">التكلفة</TableHead>
                    <TableHead className="text-center">الربح</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.products.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                        لا توجد بيانات للفترة المحددة
                      </TableCell>
                    </TableRow>
                  ) : (
                    report.products.map((p, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{p.product_name}</TableCell>
                        <TableCell className="text-center">{p.quantity_sold}</TableCell>
                        <TableCell className="text-center text-stone-500">{fc(p.cost_price)} €</TableCell>
                        <TableCell className="text-center text-blue-600">{fc(p.avg_sell_price)} €</TableCell>
                        <TableCell className="text-center font-medium">{fc(p.revenue)} €</TableCell>
                        <TableCell className="text-center text-amber-600">{fc(p.cost)} €</TableCell>
                        <TableCell className={`text-center font-bold ${p.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fc(p.profit)} €
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Expenses by Category */}
          {Object.keys(report.expenses_by_category).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Wallet className="w-5 h-5 text-red-600" />
                  المصروفات حسب الفئة
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {Object.entries(report.expenses_by_category).map(([cat, amount]) => (
                    <div key={cat} className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-red-600 truncate">{cat}</p>
                      <p className="text-lg font-bold text-red-700">{fc(amount)} €</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ProfitReportPage;
